import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import { ExpandMoreRounded, LocalHospitalRounded } from '@mui/icons-material';
import { format, isToday, isYesterday } from 'date-fns';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import DischargeInstructionCards from 'components/dashboard/discharge/DischargeInstructionCards';
import DischargePdfButton from 'components/dashboard/discharge/DischargePdfButton';
import DischargeMedicationPrompt from 'components/dashboard/discharge/DischargeMedicationPrompt';
import { useAuth } from 'hooks/useAuth';
import { db } from 'services/firebase';

const deliveryDate = (createdAt) => (createdAt?.toDate ? createdAt.toDate() : null);

// The date is what tells one visit from the next, so it is the headline.
const formatVisitDate = (date) => {
  if (!date) return 'Discharge Instructions';
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMMM d, yyyy');
};

const DischargeDelivery = ({ delivery, defaultExpanded }) => {
  const date = deliveryDate(delivery.createdAt);
  // Time always, so two visits on the same day read apart. The sender only when
  // known: a stand-in like "Your provider" repeated down the list says nothing.
  const detail = [
    date ? format(date, 'h:mm a') : null,
    [delivery.providerName, delivery.organizationName].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Accordion disableGutters defaultExpanded={defaultExpanded}>
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Stack spacing={0.25} sx={{ width: '100%', minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {formatVisitDate(date)}
          </Typography>
          {detail && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {detail}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <DischargeMedicationPrompt deliveryId={delivery.id} instructions={delivery.instructions} />
        <DischargeInstructionCards instructions={delivery.instructions} />
        <DischargePdfButton instructions={delivery.instructions} placement="bottom" />
      </AccordionDetails>
    </Accordion>
  );
};

const DischargeInstructionsPage = () => {
  const { user } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) return undefined;
    setLoading(true);
    const unsubscribe = db
      .collection('users')
      .doc(user.uid)
      .collection('dischargeInstructions')
      .orderBy('createdAt', 'desc')
      .onSnapshot(
        (snap) => {
          setDeliveries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        (err) => {
          console.error('[DischargeInstructionsPage] subscription failed:', err);
          setError('Could not load your discharge instructions. Please try again.');
          setLoading(false);
        },
      );
    return () => unsubscribe();
  }, [user?.uid]);

  return (
    <div className="inner">
      <DashboardPageHeader
        title="Discharge Instructions"
        subtitle="Your provider's instructions from each visit, newest first."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : deliveries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <LocalHospitalRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            You have no discharge instructions yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            When a provider sends you instructions after a visit, they will show up here.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {/* Newest open: it is almost always the one the patient came for. The rest stay
              collapsed so the list still reads as a history. */}
          {deliveries.map((delivery, index) => (
            <DischargeDelivery
              key={delivery.id}
              delivery={delivery}
              defaultExpanded={index === 0}
            />
          ))}
        </Stack>
      )}
    </div>
  );
};

export default DischargeInstructionsPage;
